import React from 'react';
import { Box, TextInput, Select, Collapse, Paper, Group, Button, Title, MultiSelect } from '@mantine/core';
import { IconFilter, IconX } from '@tabler/icons-react';

interface BatchFiltersProps {
  showFilters: boolean;
  combinedFilter: string;
  trainerNameFilter: string;
  statusFilter: string;
  scheduleStatusFilter: string[];
  filterAdmin: string;
  studentNameFilter: string;
  orientationFilter: string;
  bridgeCourseFilter: string;
  adminDropdown: { label: string; value: string }[];
  onCombinedFilterChange: (value: string) => void;
  onTrainerNameFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onScheduleStatusFilterChange: (value: string[]) => void;
  onFilterAdminChange: (value: string) => void;
  onStudentNameFilterChange: (value: string) => void;
  onOrientationFilterChange: (value: string) => void;
  onBridgeCourseFilterChange: (value: string) => void;
  onResetFilters: () => void;
}

export const BatchFilters: React.FC<BatchFiltersProps> = ({
  showFilters,
  combinedFilter,
  trainerNameFilter,
  statusFilter,
  scheduleStatusFilter,
  filterAdmin,
  studentNameFilter,
  orientationFilter,
  bridgeCourseFilter,
  adminDropdown,
  onCombinedFilterChange,
  onTrainerNameFilterChange,
  onStatusFilterChange,
  onScheduleStatusFilterChange,
  onFilterAdminChange,
  onStudentNameFilterChange,
  onOrientationFilterChange,
  onBridgeCourseFilterChange,
  onResetFilters
}) => {
  const yesNoOptions = [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
    { value: "Not Set", label: "Not Set" },
  ];

  return (
    <Collapse in={showFilters} transitionDuration={300}>
      <Paper
        shadow="sm"
        p="md"
        radius="md"
        withBorder
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)',
          marginBottom: '1rem',
        }}
      >
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconFilter size={20} />
            <Title order={4}>Filters</Title>
          </Group>
          <Button
            variant="subtle"
            color="gray"
            onClick={onResetFilters}
            size="sm"
          >
            <Group gap={8}>
              <IconX size={16} />
              <span>Clear Filters</span>
            </Group>
          </Button>

        </Group>

          <Box
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'flex-start',
            overflowX: 'auto',
            paddingBottom: '0.25rem',
            // keep a single row on larger screens; allow horizontal scroll when necessary
            flexWrap: 'nowrap',
          }}
        >
          {/* Combined input: supports comma-separated terms that will be searched against Batch Number and Course Name */}
          <Box style={{ flex: '0 0 220px' }}>
            <TextInput
              label="Batch No / Course"
              placeholder="e.g. B123, React Course"
              value={combinedFilter}
              onChange={(e) => onCombinedFilterChange(e.target.value)}
              size="xs"
              styles={{ label: { fontSize: 12 } }}
            />
          </Box>

          <Box style={{ flex: '0 0 140px' }}>
            <TextInput
              label="Trainer"
              placeholder="Trainer"
              value={trainerNameFilter}
              onChange={(e) => onTrainerNameFilterChange(e.target.value)}
              size="xs"
              styles={{ label: { fontSize: 12 } }}
            />
          </Box>

          <Box style={{ flex: '0 0 140px' }}>
            <Select
              label="Status"
              placeholder="Status"
              value={statusFilter}
              onChange={(value) => onStatusFilterChange(value || "")}
              data={[
                { value: "Completed", label: "Completed" },
                { value: "Hold", label: "Hold" },
                { value: "On-going", label: "On-going" },
                { value: "Not Started", label: "Not Started" },
                { value: "Absconded", label: "Absconded" },
                { value: "Discarded", label: "Discarded" }
              ]}
              clearable
              searchable
              size="xs"
              styles={{ label: { fontSize: 12 } }}
            />
          </Box>

          <Box style={{ flex: '0 0 160px' }}>
            <MultiSelect
              label="Schedule"
              placeholder="Schedule"
              value={scheduleStatusFilter}
              onChange={onScheduleStatusFilterChange}
              data={[
                { value: "Weekdays", label: "Weekdays" },
                { value: "Weekends", label: "Weekends" },
                { value: "Flexible", label: "Flexible" },
                { value: "All Days", label: "All Days" }
              ]}
              clearable
              searchable
              size="xs"
              maxDropdownHeight={200}
              hidePickedOptions
              withAsterisk={false}
              styles={{ label: { fontSize: 12 } }}
            />
          </Box>

          <Box style={{ flex: '0 0 160px' }}>
            <Select
              label="Admin"
              placeholder="Select Admin"
              value={filterAdmin || null}
              onChange={(value) => onFilterAdminChange(value || "")}
              data={adminDropdown}
              clearable
              searchable
              size="xs"
              styles={{ label: { fontSize: 12 } }}
            />
          </Box>

          <Box style={{ flex: '0 0 160px' }}>
            <TextInput
              label="Student"
              placeholder="Student"
              value={studentNameFilter}
              onChange={(e) => onStudentNameFilterChange(e.target.value)}
              size="xs"
              styles={{ label: { fontSize: 12 } }}
            />
          </Box>

          <Box style={{ flex: '0 0 130px' }}>
            <Select
              label="Orientation"
              placeholder="Orientation"
              value={orientationFilter || null}
              onChange={(value) => onOrientationFilterChange(value || "")}
              data={yesNoOptions}
              clearable
              size="xs"
              styles={{ label: { fontSize: 12 } }}
            />
          </Box>

          <Box style={{ flex: '0 0 130px' }}>
            <Select
              label="Bridge Course"
              placeholder="Bridge Course"
              value={bridgeCourseFilter || null}
              onChange={(value) => onBridgeCourseFilterChange(value || "")}
              data={yesNoOptions}
              clearable
              size="xs"
              styles={{ label: { fontSize: 12 } }}
            />
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
};

